import { useCallback } from "react";
import * as orderService from "@/supabase/order/service/orderService";
import { logError } from "@/lib/sentry";
import { toast } from "sonner";
import { OrderStatus } from "@/components/OrderControls";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";

interface UseOrderKeepActionsProps {
  currentOrderStatus: OrderStatus;
  activeCustomer: CustomerTemplate | null | undefined;
  activeCustomerId: string | null;
  customerTemplates: CustomerTemplate[];
  setLocalDateOrderTemplates: (
    updater: CustomerTemplate[] | ((prev: CustomerTemplate[]) => CustomerTemplate[])
  ) => void;
  extractOrderId: (customerId: string) => number | null;
  // 킵 되돌림의 명시적 status 쓰기 barrier(스펙 §16.10) — 낙관 갱신 직전 begin, DB 정착 후 end.
  onStatusWriteStart?: (orderId: number) => void;
  onStatusWriteEnd?: (orderId: number) => void;
}

interface UseOrderKeepActionsResult {
  handleKeepOrderChange: (checked: boolean | 'indeterminate') => void;
  handleKeepToggle: (customerId: string, currentKeepStatus: boolean) => void;
}

/**
 * 킵(is_keep) 토글 도메인 훅(P16A). 상세뷰 OrderControls의 handleKeepOrderChange와
 * 배치뷰 BatchModeView의 handleKeepToggle을 추출. 두 핸들러 body는 Home에서 바이트 그대로 이동.
 *
 * 바이트보존 high 계약(잘못 바꾸면 빌드는 통과해도 런타임 데이터 조용히 오류):
 * - handleKeepOrderChange의 '출고 완료' 분기: confirm 게이트 → is_keep+status 2필드 동시 optimistic set →
 *   Promise.all([updateOrderKeepStatus, updateOrderStatus]) 2 DB 동시호출 → early return(아래 단일-update fall-through 차단).
 * - 시그니처 (checked: boolean | 'indeterminate') + 첫 줄 typeof 가드: Radix tri-state의 'indeterminate'가 DB로 새지 않게.
 * - handleKeepToggle 실패 롤백: functional updater 안에서 인자 원본값 currentKeepStatus로 복원
 *   (handleKeepOrderChange엔 롤백 없음 = 의도된 비대칭).
 *
 * 두 핸들러 dep 배열은 의도적으로 상이(합치지 말 것). read=customerTemplates / write=setLocalDateOrderTemplates 비대칭
 * (로그인 전용 status/keep UI 전제로 customerTemplates===localDateOrderTemplates).
 */
export function useOrderKeepActions({
  currentOrderStatus,
  activeCustomer,
  activeCustomerId,
  customerTemplates,
  setLocalDateOrderTemplates,
  extractOrderId,
  onStatusWriteStart,
  onStatusWriteEnd,
}: UseOrderKeepActionsProps): UseOrderKeepActionsResult {
  const handleKeepOrderChange = useCallback((checked: boolean | 'indeterminate') => {
    if (typeof checked !== 'boolean') return;

    if (checked === true && currentOrderStatus === '출고 완료') {
      const userConfirmed = window.confirm(
        "이미 출고 완료된 주문입니다. '킵' 상태로 변경하려면 주문 상태를 '입금 완료'로 되돌려야 합니다. 변경하시겠습니까?"
      );

      if (userConfirmed) {
        const newStatus: OrderStatus = '입금 완료';

        if (activeCustomer && activeCustomerId) {
          // 명시적 status 쓰기(킵 되돌림) barrier(§16.10) — 낙관 갱신 직전 begin, DB 정착 후 end.
          const reconcileOrderId = extractOrderId(activeCustomerId);
          if (reconcileOrderId) onStatusWriteStart?.(reconcileOrderId);
          setLocalDateOrderTemplates(prev =>
            prev.map(cust =>
              cust.id === activeCustomerId ? { ...cust, is_keep: true, status: newStatus } : cust
            )
          );
          const orderId = extractOrderId(activeCustomerId);
          if (orderId) {
            Promise.all([
              orderService.updateOrderKeepStatus(orderId, true),
              orderService.updateOrderStatus(orderId, newStatus)
            ]).catch(err => {
              logError(`DB update for keep and status failed for order ${orderId}:`, err);
              alert("주문 상태 변경에 실패했습니다.");
            }).finally(() => {
              if (reconcileOrderId) onStatusWriteEnd?.(reconcileOrderId);
            });
          } else if (reconcileOrderId) {
            onStatusWriteEnd?.(reconcileOrderId);   // DB 쓰기 없음 — barrier 즉시 해제(불균형 방지)
          }
        }
      }
      return;
    }

    if (activeCustomer && activeCustomerId) {
      setLocalDateOrderTemplates(prev =>
        prev.map(cust =>
          cust.id === activeCustomerId ? { ...cust, is_keep: checked } : cust
        )
      );
      const orderId = extractOrderId(activeCustomerId);
      if (orderId) {
        orderService.updateOrderKeepStatus(orderId, checked)
          .then(() => console.log(`DB: Customer ${activeCustomer.nickname} "is_keep" updated to ${checked}.`))
          .catch(err => {
            logError(`DB update "is_keep" failed for order ${orderId}:`, err);
            alert("킵 상태 변경에 실패했습니다.");
          });
      }
    }
  }, [currentOrderStatus, activeCustomer, activeCustomerId, extractOrderId, setLocalDateOrderTemplates, onStatusWriteStart, onStatusWriteEnd]);

  const handleKeepToggle = useCallback((customerId: string, currentKeepStatus: boolean) => {
    const customer = customerTemplates.find(c => c.id === customerId);
    if (!customer) return;

    const newKeepStatus = !currentKeepStatus;

    // '출고 완료' 상태에서 킵을 하려는 경우, 경고 및 상태 변경 로직은 handleKeepOrderChange에 이미 있으므로 재사용하지 않고 단순 토글만 구현합니다.
    // 필요 시 handleKeepOrderChange의 로직을 여기에 통합할 수 있습니다.
    setLocalDateOrderTemplates(prev =>
      prev.map(cust =>
        cust.id === customerId ? { ...cust, is_keep: newKeepStatus } : cust
      )
    );

    const orderId = extractOrderId(customerId);
    if (orderId) {
      orderService.updateOrderKeepStatus(orderId, newKeepStatus)
        .then(() => toast.success(`'${customer.nickname}'님의 킵 상태가 변경되었습니다.`))
        .catch(err => {
          logError(`DB update "is_keep" failed for order ${orderId}:`, err);
          toast.error("킵 상태 변경에 실패했습니다.");
          // Revert UI
          setLocalDateOrderTemplates(prev =>
            prev.map(cust =>
              cust.id === customerId ? { ...cust, is_keep: currentKeepStatus } : cust
            )
          );
        });
    }
  }, [customerTemplates, setLocalDateOrderTemplates, extractOrderId]);

  return { handleKeepOrderChange, handleKeepToggle };
}
